import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { isAxiosError } from 'axios';
import { format } from 'date-fns';

import { apiErrorMessage } from '@/api/errors';
import { useContactFieldSuggestions } from '@/api/queries/contactFieldSuggestions';
import { contactsApi } from '@/api/queries/contacts';
import type { Contact } from '@/api/types/contacts';
import {
  Button,
  ChipEditor,
  Icon,
  Input,
  Select,
  SourcePill,
  Tag,
  TagStack,
  colorFromKey,
  useToast,
} from '@/components/ui';
import { cn } from '@/utils/cn';

import { ContactStatusPills } from '../ContactStatusPills';
import { ratingTone, reviewLabel, reviewTone, tagTone } from '../contactDisplay';
import {
  REVIEW_CHOICE_LABEL,
  buildQuickPatch,
  changedFields,
  toQuickDraft,
  validateQuickDraft,
  type QuickDraft,
  type QuickDraftErrors,
  type QuickField,
  type ReviewChoice,
} from './quickEditPatch';

const STATUS_OPTIONS: { value: QuickDraft['status']; label: string; color: string }[] = [
  { value: 'active', label: 'Active', color: 'var(--green)' },
  { value: 'inactive', label: 'Inactive', color: 'var(--slate)' },
];

const REVIEW_CHOICES: ReviewChoice[] = [
  'none',
  'requiresReview:manual',
  'requiresReview:automatic',
  'autoInvestigation',
  'reviewed',
];

const FOCUS_SELECTORS = ['input', 'select', 'textarea', '.chip-btn.active', '.chip-btn', 'button'];

interface EditState {
  baseline: QuickDraft;
  draft: QuickDraft;
  focus?: QuickField;
}

type DraftChange = <K extends QuickField>(key: K, value: QuickDraft[K]) => void;

export function ContactQuickDetails({
  contact,
  onDirtyChange,
}: {
  contact: Contact;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const toast = useToast();
  const patch = contactsApi.usePatch();
  const setReview = contactsApi.useSetReview();
  const [edit, setEdit] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);

  const changed = edit ? changedFields(edit.baseline, edit.draft) : [];
  const dirty = changed.length > 0;
  const errors: QuickDraftErrors = edit ? validateQuickDraft(edit.draft) : {};
  const hasErrors = Object.keys(errors).length > 0;

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  const startEdit = (focus?: QuickField) => {
    const baseline = toQuickDraft(contact);
    setEdit({ baseline, draft: baseline, focus });
  };

  const updateDraft = useCallback<DraftChange>((key, value) => {
    setEdit((prev) => (prev ? { ...prev, draft: { ...prev.draft, [key]: value } } : prev));
  }, []);

  const save = async () => {
    if (!edit || !dirty || hasErrors || saving || !contact.id) return;
    const { ops, review } = buildQuickPatch(edit.baseline, edit.draft, contact);
    setSaving(true);
    try {
      if (ops.length > 0) {
        await patch.mutateAsync({
          id: contact.id,
          ops,
          etag: `"${contact.audit?.version ?? 1}"`,
        });
      }
      if (review) await setReview.mutateAsync({ id: contact.id, ...review });
      setEdit(null);
      toast.show({ title: 'Contact updated', severity: 'success' });
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 409) {
        toast.show({
          severity: 'info',
          title: 'Contact was changed meanwhile',
          description: 'The latest version was loaded and your edits are kept. Save again to apply them.',
        });
      } else {
        toast.show({
          severity: 'error',
          title: 'Failed to save contact',
          description: apiErrorMessage(e, 'The contact could not be saved.'),
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="cqp-section-head">
        <div className="cqp-section-label">Details</div>
        {!edit && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Icon name="edit" size={12} />}
            onClick={() => startEdit()}
          >
            Edit
          </Button>
        )}
      </div>

      {edit ? (
        <div
          onKeyDown={(e) => {
            if (e.key === 'Escape') e.stopPropagation();
          }}
        >
          <QuickEditForm
            contact={contact}
            baseline={edit.baseline}
            draft={edit.draft}
            focus={edit.focus}
            errors={errors}
            onChange={updateDraft}
          />
          <div className="cqp-savebar">
            <span className="cqp-savebar-hint">
              {hasErrors
                ? 'Fix the highlighted fields'
                : dirty
                  ? `${changed.length} unsaved change${changed.length === 1 ? '' : 's'}`
                  : 'No changes yet'}
            </span>
            <Button variant="ghost" size="sm" disabled={saving} onClick={() => setEdit(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={saving}
              disabled={!dirty || hasErrors}
              leftIcon={!saving ? <Icon name="check" size={12} /> : undefined}
              onClick={() => void save()}
            >
              Save changes
            </Button>
          </div>
        </div>
      ) : (
        <QuickDetailsView contact={contact} onEdit={startEdit} />
      )}
    </div>
  );
}

function QuickDetailsView({
  contact,
  onEdit,
}: {
  contact: Contact;
  onEdit: (field: QuickField) => void;
}) {
  const primaryEmail = contact.contact?.primaryEmail ?? '';
  const otherEmails = (contact.contact?.emails ?? [])
    .map((e) => e.email)
    .filter((e): e is string => !!e && e.toLowerCase() !== primaryEmail.toLowerCase());
  const phones = (contact.contact?.phones ?? [])
    .map((p) => p.number)
    .filter((n): n is string => !!n);
  const tags = contact.tags ?? [];
  const sources = contact.sources ?? [];
  const score = contact.rating?.score;
  const review = reviewLabel(contact.review?.status);

  return (
    <div className="cqp-grid">
      <ViewRow label="Email" field="email" onEdit={onEdit}>
        {primaryEmail ? <span>{primaryEmail}</span> : <Empty />}
        {otherEmails.map((e) => (
          <span key={e} className="cqp-sub">
            {e}
          </span>
        ))}
      </ViewRow>
      <ViewRow label="Phone" field="phone" onEdit={onEdit}>
        {phones.length > 0 ? (
          phones.map((p, i) => (
            <span key={`${p}-${i}`} className={i > 0 ? 'cqp-sub' : undefined}>
              {p}
            </span>
          ))
        ) : (
          <Empty />
        )}
      </ViewRow>
      <TextRow label="Position" field="position" value={contact.profile?.position} onEdit={onEdit} />
      <TextRow
        label="Occupation"
        field="occupation"
        value={contact.profile?.occupation}
        onEdit={onEdit}
      />
      <TextRow label="Interest" field="interest" value={contact.profile?.interest} onEdit={onEdit} />
      <TextRow
        label="Residence"
        field="residence"
        value={contact.profile?.residence}
        onEdit={onEdit}
      />
      <ViewRow label="Tags" field="tags" onEdit={onEdit}>
        {tags.length > 0 ? (
          <TagStack>
            {tags.map((t) => (
              <Tag key={t} tone={tagTone(t)}>
                {t}
              </Tag>
            ))}
          </TagStack>
        ) : (
          <Empty />
        )}
      </ViewRow>
      <ViewRow label="Sources" field="sources" onEdit={onEdit}>
        {sources.length > 0 ? (
          <TagStack>
            {sources.map((s) => (
              <SourcePill key={s} label={s} color={colorFromKey(s)} />
            ))}
          </TagStack>
        ) : (
          <Empty />
        )}
      </ViewRow>
      <ViewRow label="Attachments">
        <AttachmentsValue contact={contact} />
      </ViewRow>
      <ViewRow label="Rating" field="rating" onEdit={onEdit}>
        {score === undefined || score === null ? (
          <Empty />
        ) : (
          <InlineGroup>
            <Tag tone={ratingTone(score)}>{Math.round(score)}</Tag>
            {contact.rating?.engagement && (
              <span className="cqp-sub">{contact.rating.engagement} engagement</span>
            )}
          </InlineGroup>
        )}
      </ViewRow>
      <ViewRow label="Review" field="review" onEdit={onEdit}>
        {review ? (
          <InlineGroup>
            <Tag tone={reviewTone(contact.review?.status)}>{review}</Tag>
            {contact.review?.mode && <span className="cqp-sub">{contact.review.mode}</span>}
          </InlineGroup>
        ) : (
          <Empty />
        )}
      </ViewRow>
      <ViewRow label="Status" field="status" onEdit={onEdit}>
        <ContactStatusPills status={contact.status} />
      </ViewRow>
      <ViewRow label="Last updated">
        <LastUpdatedValue contact={contact} />
      </ViewRow>
    </div>
  );
}

function QuickEditForm({
  contact,
  baseline,
  draft,
  focus,
  errors,
  onChange,
}: {
  contact: Contact;
  baseline: QuickDraft;
  draft: QuickDraft;
  focus?: QuickField;
  errors: QuickDraftErrors;
  onChange: DraftChange;
}) {
  const positionSug = useContactFieldSuggestions('profile.position');
  const occupationSug = useContactFieldSuggestions('profile.occupation');
  const interestSug = useContactFieldSuggestions('profile.interest');
  const residenceSug = useContactFieldSuggestions('profile.residence');
  const tagSug = useContactFieldSuggestions('tags');
  const sourceSug = useContactFieldSuggestions('sources');
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const row = gridRef.current?.querySelector<HTMLElement>(`[data-field="${focus ?? 'firstName'}"]`);
    if (!row) return;
    for (const selector of FOCUS_SELECTORS) {
      const target = row.querySelector<HTMLElement>(selector);
      if (target) {
        target.focus();
        return;
      }
    }
  }, [focus]);

  const reviewChoices =
    baseline.review === 'investigated' ? [...REVIEW_CHOICES, 'investigated' as const] : REVIEW_CHOICES;

  return (
    <div className="cqp-grid" ref={gridRef}>
      <EditRow label="First name" field="firstName">
        <Input value={draft.firstName} onChange={(e) => onChange('firstName', e.target.value)} />
      </EditRow>
      <EditRow label="Last name" field="lastName">
        <Input value={draft.lastName} onChange={(e) => onChange('lastName', e.target.value)} />
      </EditRow>
      <EditRow label="Email" field="email" error={errors.email}>
        <Input
          type="email"
          error={!!errors.email}
          value={draft.email}
          onChange={(e) => onChange('email', e.target.value)}
        />
      </EditRow>
      <EditRow label="Phone" field="phone">
        <Input type="tel" value={draft.phone} onChange={(e) => onChange('phone', e.target.value)} />
      </EditRow>
      <EditRow label="Position" field="position">
        <SuggestInput
          value={draft.position}
          suggestions={positionSug.suggestions}
          onChange={(v) => onChange('position', v)}
        />
      </EditRow>
      <EditRow label="Occupation" field="occupation">
        <SuggestInput
          value={draft.occupation}
          suggestions={occupationSug.suggestions}
          onChange={(v) => onChange('occupation', v)}
        />
      </EditRow>
      <EditRow label="Interest" field="interest">
        <SuggestInput
          value={draft.interest}
          suggestions={interestSug.suggestions}
          onChange={(v) => onChange('interest', v)}
        />
      </EditRow>
      <EditRow label="Residence" field="residence">
        <SuggestInput
          value={draft.residence}
          suggestions={residenceSug.suggestions}
          onChange={(v) => onChange('residence', v)}
        />
      </EditRow>
      <EditRow label="Tags" field="tags">
        <ChipEditor
          values={draft.tags}
          onChange={(v) => onChange('tags', v)}
          suggestions={tagSug.suggestions}
          addLabel="Add tag"
          tone="brand"
        />
      </EditRow>
      <EditRow label="Sources" field="sources">
        <ChipEditor
          values={draft.sources}
          onChange={(v) => onChange('sources', v)}
          suggestions={sourceSug.suggestions}
          addLabel="Add source"
          tone="teal"
        />
      </EditRow>
      <ViewRow label="Attachments">
        <AttachmentsValue contact={contact} />
      </ViewRow>
      <EditRow label="Rating" field="rating" error={errors.rating}>
        <Input
          type="number"
          min={0}
          max={100}
          step="any"
          placeholder="0–100"
          error={!!errors.rating}
          value={draft.rating}
          onChange={(e) => onChange('rating', e.target.value)}
        />
      </EditRow>
      <EditRow label="Review" field="review">
        <Select
          value={draft.review}
          options={reviewChoices.map((value) => ({ value, label: REVIEW_CHOICE_LABEL[value] }))}
          onChange={(e) => onChange('review', e.target.value as ReviewChoice)}
        />
      </EditRow>
      <EditRow label="Status" field="status">
        <div className="cqp-status-row">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={cn('chip-btn', draft.status === opt.value && 'active')}
              onClick={() => onChange('status', opt.value)}
            >
              <span className="cqp-status-dot" style={{ background: opt.color }} />
              {opt.label}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={draft.doNotContact}
            className={cn('chip-btn', draft.doNotContact && 'active')}
            onClick={() => onChange('doNotContact', !draft.doNotContact)}
          >
            <span className="cqp-status-dot" style={{ background: 'var(--red)' }} />
            Do not contact
          </button>
        </div>
      </EditRow>
      <ViewRow label="Last updated">
        <LastUpdatedValue contact={contact} />
      </ViewRow>
    </div>
  );
}

function ViewRow({
  label,
  field,
  onEdit,
  children,
}: {
  label: string;
  field?: QuickField;
  onEdit?: (field: QuickField) => void;
  children: ReactNode;
}) {
  const open = field && onEdit ? () => onEdit(field) : undefined;
  return (
    <>
      <div className="cqp-label">{label}</div>
      <div
        className={cn('cqp-value', open && 'editable')}
        role={open ? 'button' : undefined}
        tabIndex={open ? 0 : undefined}
        title={open ? `Edit ${label.toLowerCase()}` : undefined}
        onClick={
          open
            ? () => {
                if (window.getSelection()?.toString()) return;
                open();
              }
            : undefined
        }
        onKeyDown={
          open
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  open();
                }
              }
            : undefined
        }
      >
        {children}
      </div>
    </>
  );
}

function TextRow({
  label,
  field,
  value,
  onEdit,
}: {
  label: string;
  field: QuickField;
  value?: string | null;
  onEdit: (field: QuickField) => void;
}) {
  return (
    <ViewRow label={label} field={field} onEdit={onEdit}>
      {value ? <span>{value}</span> : <Empty />}
    </ViewRow>
  );
}

function EditRow({
  label,
  field,
  error,
  children,
}: {
  label: string;
  field: QuickField;
  error?: string;
  children: ReactNode;
}) {
  return (
    <>
      <div className="cqp-label">{label}</div>
      <div className="cqp-value editing" data-field={field} style={{ alignItems: 'stretch' }}>
        {children}
        {error && <span className="cqp-field-error">{error}</span>}
      </div>
    </>
  );
}

function SuggestInput({
  value,
  suggestions,
  onChange,
}: {
  value: string;
  suggestions: string[];
  onChange: (next: string) => void;
}) {
  const listId = useId();
  return (
    <>
      <Input
        value={value}
        list={suggestions.length ? listId : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {suggestions.length > 0 && (
        <datalist id={listId}>
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      )}
    </>
  );
}

function AttachmentsValue({ contact }: { contact: Contact }) {
  const attachments = contact.attachments ?? [];
  if (attachments.length === 0) return <Empty />;
  return (
    <>
      {attachments.map((a, i) => (
        <InlineGroup key={a.fileId || `${a.name}-${i}`}>
          {a.category && <Tag tone="brand">{a.category === 'cv' ? 'CV' : a.category}</Tag>}
          <span>{a.name}</span>
        </InlineGroup>
      ))}
    </>
  );
}

function LastUpdatedValue({ contact }: { contact: Contact }) {
  const raw = contact.audit?.updatedAt;
  const date = raw ? new Date(raw) : undefined;
  if (!date || Number.isNaN(date.getTime())) return <Empty />;
  return (
    <>
      <span title={date.toISOString()}>{format(date, 'd MMM yyyy, HH:mm')}</span>
      {contact.audit?.updatedBy && <span className="cqp-sub">{contact.audit.updatedBy}</span>}
    </>
  );
}

function InlineGroup({ children }: { children: ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {children}
    </span>
  );
}

function Empty() {
  return <span className="cqp-muted">—</span>;
}
